import React, { useState } from 'react'
import BreadCrumbs from '../../../../components/common/BreadCrumbs/BreadCrumbs'
import { useNavigate } from 'react-router-dom'
import SearchForm, { SearchOption, FieldConfig } from './SearchForm'
import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import { ProxyMode } from '../../../../components/common/config/adminConfig'
import {
  searchRequestorProxy,
  selectRequestorProxyLoading,
} from '../../../../store/slices/admin/requestorProxySlice'
import {
  searchApproverProxy,
  selectApproverProxyLoading,
} from '../../../../store/slices/admin/approverProxySlice'
import {
  searchFloatEmployees,
  selectFloatEmployeeLoading,
} from '../../../../store/slices/admin/floatEmployeeSlice'

/** Props for the ProxySearch form component. */
interface ProxySearchProps {
  /** Page heading rendered in the breadcrumb and as an h2. */
  title: string
  /**
   * Proxy mode — determines which search API is dispatched.
   * `'approver'` dispatches the approver-proxy search (searchAP); every other
   * mode falls back to the requestor-proxy search (searchRP). Defaults to
   * `'requestor'` so existing requestor/float usage is unchanged.
   */
  mode?: ProxyMode
}

/** Trimmed field values collected from the search form before validation. */
interface TrimmedSearchValues {
  nuid: string
  firstName: string
  lastName: string
  groupId: string
}

/** Minimum characters required for the last-name search field. */
const LAST_NAME_MIN_LENGTH = 2

/** Allows only alphanumeric characters (0-9, a-z, A-Z) — no spaces or punctuation. */
const STRICT_ALPHANUMERIC_REGEX = /^[a-zA-Z0-9]*$/

/** Shared validation message for disallowed input. */
const ALPHANUMERIC_ERROR_MESSAGE =
  'Please only enter alphanumeric characters (0-9 a-z A-Z).'

/**
 * Search form for finding a primary approver, requestor, or group
 * to assign a proxy. Dispatches the mode-appropriate search thunk
 * ({@link searchApproverProxy} for approver mode, {@link searchRequestorProxy}
 * otherwise) on a valid submission and reflects the loading state from the
 * matching Redux slice.
 */
const ProxySearch: React.FC<ProxySearchProps> = ({ title, mode = 'requestor' }) => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const isApproverMode = mode === 'approver'
  const isFloatMode = mode === 'float'

  // Loading comes from whichever slice backs the active mode's search.
  const requestorLoading = useAppSelector(selectRequestorProxyLoading)
  const approverLoading = useAppSelector(selectApproverProxyLoading)
  const floatLoading = useAppSelector(selectFloatEmployeeLoading)

  let isLoading: boolean
  if (isApproverMode) isLoading = approverLoading
  else if (isFloatMode) isLoading = floatLoading
  else isLoading = requestorLoading

  const [searchBy, setSearchBy] = useState<string>('nuid')
  const [nuid, setNuid] = useState<string>('')
  const [firstName, setFirstName] = useState<string>('')
  const [lastName, setLastName] = useState<string>('')
  const [groupId, setGroupId] = useState<string>('')
  const [errors, setErrors] = useState<Record<string, string>>({})

  const handlePreviousClick = () => navigate('/admin')

  const searchOptions: SearchOption[] = [
    { key: 'nuid', label: 'Primary Approver NUID' },
    { key: 'name', label: 'Primary Approver First or Last Name' },
    { key: 'group', label: 'Primary Approval Group ID' },
  ]

  const fieldMeta: Record<string, FieldConfig> = {
    nuid: { id: 1, label: 'Primary Approver NUID', name: 'nuid', placeholder: 'Enter NUID' },
    firstName: {
      id: 2,
      label: 'Primary Approver First Name',
      name: 'firstName',
      placeholder: 'First name',
      rowGroup: 'names',
      containerClass: 'w-full desktop:w-[350px]',
    },
    lastName: {
      id: 3,
      label: 'Primary Approver Last Name',
      name: 'lastName',
      placeholder: 'Last name',
      rowGroup: 'names',
      containerClass: 'w-full desktop:w-[350px]',
      required: true,
    },
    groupId: { id: 4, label: 'Primary Approval Group ID', name: 'groupId', placeholder: 'Enter Group ID' },
  }

  const fieldsByMode: Record<string, string[]> = {
    nuid: ['nuid'],
    name: ['firstName', 'lastName'],
    group: ['groupId'],
  }

  const values = { nuid, firstName, lastName, groupId }

  /** Clears validation errors and updates the active search mode. */
  const handleSearchTypeChange = (type: string) => {
    setSearchBy(type)
    setNuid('')
    setFirstName('')
    setLastName('')
    setGroupId('')
    setErrors({})
  }

  /**
   * Updates the individual field value and re-validates that field.
   * Only recomputes when the field already has a visible error, so a value
   * that's still invalid after an edit (e.g. one special character removed
   * from several) keeps showing an error instead of clearing prematurely.
   * Clearing the field back to empty always clears the error immediately
   * rather than reactively surfacing a "required" message — that check only
   * runs again on the next Search click.
   */
  const handleFieldChange = (fieldName: string, value: string) => {
    if (fieldName === 'nuid') setNuid(value)
    else if (fieldName === 'firstName') setFirstName(value)
    else if (fieldName === 'lastName') setLastName(value)
    else if (fieldName === 'groupId') setGroupId(value)

    setErrors((prev) => {
      if (!prev[fieldName]) return prev

      const trimmedValue = value.trim()
      const { [fieldName]: _, ...rest } = prev
      if (!trimmedValue) return rest

      const trimmed: TrimmedSearchValues = {
        nuid: fieldName === 'nuid' ? trimmedValue : nuid.trim(),
        firstName: fieldName === 'firstName' ? trimmedValue : firstName.trim(),
        lastName: fieldName === 'lastName' ? trimmedValue : lastName.trim(),
        groupId: fieldName === 'groupId' ? trimmedValue : groupId.trim(),
      }

      const revalidated = buildValidationErrors(trimmed)
      return revalidated[fieldName] ? { ...rest, [fieldName]: revalidated[fieldName] } : rest
    })
  }

  /**
   * Builds validation errors for the current search mode.
   *
   * Rules:
   * - NUID / Group ID search: field must not be empty.
   * - Name search:
   *   1. First Name and Last Name must contain only alphanumeric characters (and spaces).
   *   2. Last Name is required.
   *   3. Last Name must be at least {@link LAST_NAME_MIN_LENGTH} characters.
   *
   * @param trimmed - Trimmed field values.
   * @returns A map of field → error message (empty when valid).
   */
const validateNameSearch = (
  trimmed: TrimmedSearchValues
): Record<string, string> => {
  const errors: Record<string, string> = {}

  if (trimmed.firstName && !STRICT_ALPHANUMERIC_REGEX.test(trimmed.firstName)) {
    errors.firstName = ALPHANUMERIC_ERROR_MESSAGE
  }

  if (trimmed.lastName && !STRICT_ALPHANUMERIC_REGEX.test(trimmed.lastName)) {
    errors.lastName = ALPHANUMERIC_ERROR_MESSAGE
  }

  if (!errors.lastName) {
    if (!trimmed.lastName) {
      errors.lastName = 'Please enter Last Name value for search'
    } else if (trimmed.lastName.length < LAST_NAME_MIN_LENGTH) {
      errors.lastName = 'Last name must be at least 2 characters'
    }
  }

  return errors
}

const buildValidationErrors = (
  trimmed: TrimmedSearchValues
): Record<string, string> => {
  const nextErrors: Record<string, string> = {}

  switch (searchBy) {
    case 'nuid':
      if (!trimmed.nuid) {
        nextErrors.nuid = 'Please enter search key value'
      } else if (!STRICT_ALPHANUMERIC_REGEX.test(trimmed.nuid)) {
        nextErrors.nuid = ALPHANUMERIC_ERROR_MESSAGE
      }
      break

    case 'group':
      if (!trimmed.groupId) {
        nextErrors.groupId = 'Please enter search key value'
      } else if (!STRICT_ALPHANUMERIC_REGEX.test(trimmed.groupId)) {
        nextErrors.groupId = ALPHANUMERIC_ERROR_MESSAGE
      }
      break

    case 'name':
      return validateNameSearch(trimmed)
  }

  return nextErrors
}
  /**
   * Dispatches the approver-proxy search (searchAP) with the contract-shaped
   * query params for the current mode.
   *
   * @param trimmed - Trimmed field values.
   */
  const dispatchApproverSearch = (trimmed: TrimmedSearchValues) => {
    if (searchBy === 'name') {
      dispatch(
        searchApproverProxy({
          searchOption: 'name',
          searchLName: trimmed.lastName,
          searchFName: trimmed.firstName,
        })
      )
    } else {
      // 'group' radio maps to the 'approvalGrpId' searchOption param.
      dispatch(
        searchApproverProxy({
          searchOption: searchBy === 'group' ? 'approvalGrpId' : 'nuid',
          searchValue: searchBy === 'nuid' ? trimmed.nuid : trimmed.groupId,
        })
      )
    }
  }

  /**
   * Dispatches the requestor-proxy search (searchRP) with the query params
   * for the current mode (used for requestor and float modes).
   *
   * @param trimmed - Trimmed field values.
   */
  const dispatchRequestorSearch = (trimmed: TrimmedSearchValues) => {
    if (searchBy === 'name') {
      dispatch(
        searchRequestorProxy({
          searchOption: 'name',
          searchFName: trimmed.firstName,
          searchLName: trimmed.lastName,
        })
      )
    } else {
      // 'group' radio maps to 'approvalGrpId' as the API searchOption param
      dispatch(
        searchRequestorProxy({
          searchOption: searchBy === 'group' ? 'approvalGrpId' : 'nuid',
          searchValue: searchBy === 'nuid' ? trimmed.nuid : trimmed.groupId,
        })
      )
    }
  }

  /**
   * Dispatches the float-employee search (searchFE) with the contract-shaped
   * query params for float mode.
   *
   * @param trimmed - Trimmed field values.
   */
  const dispatchFloatSearch = (trimmed: TrimmedSearchValues) => {
    if (searchBy === 'name') {
      dispatch(
        searchFloatEmployees({
          searchOption: 'name',
          searchLName: trimmed.lastName,
          searchFName: trimmed.firstName,
        })
      )
    } else {
      dispatch(
        searchFloatEmployees({
          searchOption: searchBy === 'group' ? 'approvalGrpId' : 'nuid',
          searchValue: searchBy === 'nuid' ? trimmed.nuid : trimmed.groupId,
        })
      )
    }
  }

  /**
   * Validates the form and dispatches the mode-appropriate proxy search thunk.
   * Inputs are trimmed before validation and dispatch. When validation fails no
   * API call is made and the field errors are surfaced inline.
   */
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()

    const trimmed: TrimmedSearchValues = {
      nuid: nuid.trim(),
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      groupId: groupId.trim(),
    }

    const nextErrors = buildValidationErrors(trimmed)
    setErrors(nextErrors)
    if (Object.keys(nextErrors).length > 0) return

    if (isApproverMode) {
      dispatchApproverSearch(trimmed)
    } else if (isFloatMode) {
      dispatchFloatSearch(trimmed)
    } else {
      dispatchRequestorSearch(trimmed)
    }
  }

  return (
    <div className="mb-6">
      <BreadCrumbs previousPage="Administration" currentPage={title} onPreviousClick={handlePreviousClick} />
      <h1 className="h1 mb-12!">{title}</h1>
      <div className='ml-[4px]!'>
      <h2 className='h2'>Select an Approver or Group</h2>
      <SearchForm
        searchOptions={searchOptions}
        fieldsByMode={fieldsByMode}
        fieldMeta={fieldMeta}
        values={values}
        errors={errors}
        searchBy={searchBy}
        onSearchTypeChange={handleSearchTypeChange}
        onFieldChange={handleFieldChange}
        onSearch={handleSearch}
        isLoading={isLoading}
        columnLayout={true}
        formId="searchForm"
      />
      </div>
    </div>
  )
}

export default ProxySearch
